export const queryClient = {
  invalidateQueries: () => {
    console.log('Cache invalidated');
  },
};

class ApiRequestError extends Error {
  status: number;
  backendMessage: string | undefined;

  constructor(status: number, message: string, backendMessage?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.backendMessage = backendMessage;
  }
}

function getSafeHttpErrorMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Bad request';
    case 401:
      return 'Unauthorized';
    case 403:
      return 'Forbidden';
    case 404:
      return 'Not found';
    case 409:
      return 'Conflict';
    case 422:
      return 'Validation error';
    default:
      return status >= 500 ? 'Server error' : `HTTP error (${status})`;
  }
}

export function appendScopeToApiUrl(url: string): string {
  if (typeof window === 'undefined') return url;

  const scope = new URLSearchParams(window.location.search).get('scope')?.trim();
  if (!scope) return url;

  try {
    const target = new URL(url, window.location.origin);
    if (target.origin !== window.location.origin || !target.pathname.startsWith('/api/')) {
      return url;
    }
    if (target.searchParams.has('scope')) return url;

    target.searchParams.set('scope', scope);
    return /^https?:\/\//i.test(url)
      ? target.toString()
      : `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return url;
  }
}

export async function apiRequest(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  url: string,
  data?: unknown,
): Promise<Response> {
  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  };

  if (data) {
    config.body = JSON.stringify(data);
  }

  const response = await fetch(appendScopeToApiUrl(url), config);

  if (!response.ok) {
    let backendMessage: string | undefined;
    const contentType = response.headers.get('content-type') || '';
    try {
      if (contentType.includes('application/json')) {
        const payloadRaw = (await response.json()) as unknown;
        if (payloadRaw && typeof payloadRaw === 'object') {
          const payload = payloadRaw as { error?: string; message?: string };
          if (typeof payload.error === 'string' && payload.error.trim()) {
            backendMessage = payload.error;
          } else if (typeof payload.message === 'string' && payload.message.trim()) {
            backendMessage = payload.message;
          }
        } else if (typeof payloadRaw === 'string' && payloadRaw.trim()) {
          backendMessage = payloadRaw;
        }
      } else {
        const text = await response.text();
        if (text.trim()) {
          backendMessage = text;
        }
      }
    } catch {
      // use safe fallback message
    }
    throw new ApiRequestError(
      response.status,
      getSafeHttpErrorMessage(response.status),
      backendMessage,
    );
  }

  return response;
}
